import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Copy, FileSpreadsheet, FileText, Mail, Bookmark, Check, Loader2, Zap, Ruler, ChevronDown } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  parseAnswer, exportBlocks, tableToHtml, tableToTsv, copyRich, answerToCopy, downloadBlob,
} from "./answer";
import "./analyst.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERIF = "'Literata', Georgia, serif";
const SANS = "var(--rl-font-body), sans-serif";

// Only internal RockLink destinations may become clickable links.
const ALLOWED_PATHS = new Set([
  "/", "/po-received", "/procurement", "/manufacturing", "/qc-check",
  "/dispatch", "/completed", "/changelog", "/blocked-board",
]);
const internalTarget = (url) => {
  if (typeof url === "string" && /^item:[\w-]{8,64}$/.test(url)) return { itemId: url.slice(5) };
  if (typeof url !== "string" || !url.startsWith("/") || url.startsWith("//")) return null;
  try {
    const u = new URL(url, "http://rocklink.local");
    if (u.host !== "rocklink.local" || !ALLOWED_PATHS.has(u.pathname)) return null;
    return { path: u.pathname, item: u.searchParams.get("item"), q: u.searchParams.get("q") };
  } catch { return null; }
};

const INLINE = /(!\[[^\]]*\]\([^)]*\)|\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|`[^`]+`)/g;

function Inline({ text, onLink, highlight = false }) {
  const parts = String(text ?? "").split(INLINE).filter(Boolean);
  return parts.map((p, i) => {
    if (p.startsWith("![")) return null;
    const link = p.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const t = internalTarget(link[2]);
      if (!t) return <span key={i}>{link[1]}</span>;
      return (
        <button key={i} type="button" onClick={() => onLink(t)} data-testid="analyst-link"
          className="whitespace-nowrap font-mono-data font-semibold text-orange-700 dark:text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:decoration-orange-500 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
          {link[1]}
        </button>
      );
    }
    if (p.startsWith("**")) return <strong key={i} className={`font-semibold ${highlight ? "an-hl" : ""}`}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("`")) return <code key={i} className="px-1 rounded bg-[hsl(var(--muted))] text-[12px]">{p.slice(1, -1)}</code>;
    return <span key={i}>{p}</span>;
  });
}

// Stage words → RockLink's --stage-* colours.
const STAGE_VAR = {
  "po received": "received", "po rec.": "received", procurement: "procurement", manufacturing: "manufacturing",
  mfg: "manufacturing", "quality control": "qc", qc: "qc", dispatch: "dispatch", completed: "completed",
};
const NUMERIC = /^\s*[-−]?\$?\s?[-−]?[\d,]+(\.\d+)?\s*(%|d|days|pcs)?\s*$/i;
const LATE = /^\s*[-−]\s?\d+\s*(d|days)?\s*$/i;
const MONEY_HEAD = /value|usd|amount|spend|\$/i;
const toNum = (v) => Number(String(v ?? "").replace(/[^\d.-]/g, "")) || 0;

// "PO-2026-113/001" → a link resolved by PO + line (the model never writes ids).
function PoCell({ value, onLink }) {
  const v = String(value ?? "").replace(/\s*\/\s*/g, "/");
  if (/\]\(/.test(v)) return <Inline text={v} onLink={onLink} />;
  const token = v.split(/\s+/).reverse().find((x) => x.includes("/"));
  if (!token) return <span className="font-mono-data">{v}</span>;
  const cut = token.lastIndexOf("/");
  const po = token.slice(0, cut), line = token.slice(cut + 1);
  return (
    <button type="button" onClick={() => onLink({ po, line })} data-testid="analyst-link"
      className="whitespace-nowrap font-mono-data font-semibold text-orange-700 dark:text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:decoration-orange-500 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
      {token}
    </button>
  );
}

function Cell({ value, onLink, stageCol }) {
  const v = String(value ?? "");
  if (/^\s*expedite\s*$/i.test(v)) {
    return (
      <span className="rl-gold-badge inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded whitespace-nowrap">
        <Zap className="w-3 h-3" />Expedite
      </span>
    );
  }
  const stage = stageCol && STAGE_VAR[v.trim().toLowerCase()];
  if (/^\s*\d{4}-\d{2}-\d{2}/.test(v)) return <span className="whitespace-nowrap font-mono-data">{v}</span>;
  if (stage) {
    return (
      <span className="inline-block px-2 py-[1px] rounded-full text-[11px] font-semibold whitespace-nowrap"
        style={{ background: `hsl(var(--stage-${stage}) / 0.14)`, color: `hsl(var(--stage-${stage}))` }}>
        {v}
      </span>
    );
  }
  if (LATE.test(v)) return <span className="font-mono-data font-semibold text-red-600 dark:text-red-400">{v}</span>;
  if (NUMERIC.test(v)) return <span className="font-mono-data font-semibold">{v}</span>;
  return <Inline text={v} onLink={onLink} />;
}

// Small text action with feedback (✓ after success, optional progress fill).
function QuietAction({ icon: Icon, label, doneLabel, onRun, fill = false, testid }) {
  const [state, setState] = useState("idle");
  const ref = useRef(null);
  const run = async () => {
    if (state === "busy") return;
    setState("busy");
    const ok = await onRun(ref.current);
    setState(ok === false ? "idle" : "done");
    if (ok !== false) setTimeout(() => setState("idle"), 1800);
  };
  return (
    <button ref={ref} type="button" onClick={run} data-testid={testid}
      className="relative overflow-hidden inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-[12px] font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 transition-colors">
      {fill && <span className={`an-fill ${state !== "idle" ? "on" : ""}`} aria-hidden="true" />}
      <span className={`relative inline-flex ${state === "done" ? "an-pop" : ""}`}>
        {state === "done" ? <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          : state === "busy" && !fill ? <Loader2 className="w-4 h-4 animate-spin an-muted" />
          : <Icon className="w-4 h-4 an-muted" />}
      </span>
      <span className="relative">{state === "done" ? doneLabel : label}</span>
    </button>
  );
}

function Kpi({ item, animate, delay }) {
  const m = String(item.value).match(/^(\D*?)(-?[\d,]*\.?\d+)(.*)$/);
  const target = m ? Number(m[2].replace(/,/g, "")) : null;
  const decimals = m && m[2].includes(".") ? m[2].split(".")[1].length : 0;
  const [shown, setShown] = useState(animate && target != null ? 0 : target);
  useEffect(() => {
    if (!animate || target == null) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || document.documentElement.dataset.flat === "1") {
      setShown(target); return undefined;
    }
    let raf; const t0 = performance.now() + delay;
    const tick = (t) => {
      const k = Math.min(1, Math.max(0, (t - t0) / 750));
      setShown(target * (1 - Math.pow(1 - k, 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animate, target, delay]);
  const text = m && shown != null
    ? `${m[1]}${Number(shown).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${m[3]}`
    : item.value;
  return (
    <div className="an-in rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3.5 py-2.5" style={{ animationDelay: `${delay}ms` }}>
      <div className="text-[11.5px] an-muted truncate" style={{ fontFamily: SANS }}>{item.label}</div>
      <div className="font-mono-data text-[22px] font-bold leading-tight tracking-tight tabular-nums">{text}</div>
    </div>
  );
}

function KpiTiles({ items, animate }) {
  return (
    <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }} data-testid="analyst-kpis">
      {items.map((k, i) => <Kpi key={i} item={k} animate={animate} delay={i * 90} />)}
    </div>
  );
}

function ReportTable({ table, title, asOf, onLink }) {
  const numericCols = useMemo(() => table.columns.map((_, i) =>
    table.rows.length > 0 && table.rows.every((r) => !r[i] || NUMERIC.test(r[i]) || LATE.test(r[i]))), [table]);
  // One value column gets bars: the first numeric column whose header reads like money.
  const barCol = useMemo(() => table.columns.findIndex((h, i) => numericCols[i] && MONEY_HEAD.test(h)), [table, numericCols]);
  const barMax = useMemo(() => (barCol < 0 ? 0 : Math.max(...table.rows.map((r) => Math.abs(toNum(r[barCol]))), 0)), [table, barCol]);
  const narrow = table.columns.length <= 5;
  const poCol = useMemo(() => table.columns.findIndex((h) => /^\s*\**\s*po\b/i.test(String(h))), [table]);
  const stageCols = useMemo(() => table.columns.map((h) => /stage|status/i.test(String(h))), [table]);
  // Long free-text columns (description, blocker, reason, note) get room so rows don't tower.
  const textWide = useMemo(() => table.columns.map((h, i) => !numericCols[i] && /descr|item|blocker|reason|note|action/i.test(h)), [table, numericCols]);

  const copy = async () => copyRich(tableToHtml(table), tableToTsv(table));
  const excel = async () => {
    try {
      const { data } = await axios.post(`${API}/ai/table.xlsx`,
        { title, columns: table.columns, rows: table.rows, as_of: asOf || "" }, { responseType: "blob" });
      downloadBlob(data, `${(title || "RockLink report").replace(/[^\w -]+/g, "").slice(0, 60) || "RockLink report"}.xlsx`);
      return true;
    } catch { toast.error("Couldn't build the Excel file"); return false; }
  };

  return (
    <div style={{ fontFamily: SANS }} data-testid="analyst-table">
      <div className={`${narrow ? "inline-block max-w-full" : "block"} rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden align-top`}>
        <div className="overflow-x-auto max-h-[560px]">
          <table className={`${narrow ? "w-auto min-w-[360px]" : "w-full"} text-[12.5px] font-medium text-[hsl(var(--foreground))]`}>
            <thead className="rl-thead sticky top-0 z-[1]">
              <tr>
                {table.columns.map((h, j) => (
                  <th key={j} scope="col" className={`px-3.5 py-2 font-semibold whitespace-nowrap an-muted ${numericCols[j] ? "text-right" : "text-left"} ${j === barCol ? "min-w-[110px]" : ""}`}>
                    <Inline text={h} onLink={onLink} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, j) => (
                <tr key={j} className={`an-row border-t border-[hsl(var(--border))]/70 ${r.some((v) => /^\s*expedite\s*$/i.test(String(v ?? ""))) ? "rl-expedite-row" : "hover:bg-orange-500/[0.04]"}`} style={{ "--i": Math.min(j, 20) }}>
                  {table.columns.map((_, k) => (
                    <td key={k} className={`px-3.5 py-2 align-top ${numericCols[k] ? "text-right whitespace-nowrap" : ""} ${textWide[k] ? (/descr|item/i.test(table.columns[k]) ? "min-w-[200px]" : "min-w-[160px]") : ""}`}>
                      {k === poCol ? <PoCell value={r[k]} onLink={onLink} /> : <Cell value={r[k]} onLink={onLink} stageCol={stageCols[k]} />}
                      {k === barCol && barMax > 0 && (
                        <span className="an-track" aria-hidden="true">
                          <span className="an-bar" style={{ "--w": `${Math.max(3, (Math.abs(toNum(r[k])) / barMax) * 100)}%` }} />
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center gap-0.5 mt-1">
        <QuietAction icon={Copy} label="Copy table" doneLabel="Copied" onRun={copy} testid="analyst-table-copy" />
        <QuietAction icon={FileSpreadsheet} label="Excel" doneLabel="Downloaded" onRun={excel} fill testid="analyst-table-excel" />
        {table.rows.length > 10 && <span className="ml-2 text-[11.5px] an-muted"><span className="font-mono-data">{table.rows.length}</span> rows</span>}
      </div>
    </div>
  );
}

function EmailDialog({ open, onClose, payload }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [subject, setSubject] = useState(payload.title);
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const list = to.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!list.length) { setErr("Add at least one address"); return; }
    const bad = list.filter((a) => !/^[a-z0-9._%+-]+@rockpecker\.com$/.test(a));
    if (bad.length) { setErr(`Only @rockpecker.com addresses — check ${bad[0]}`); return; }
    setSending(true);
    try {
      await axios.post(`${API}/ai/email-table`, { ...payload, to: list, subject, note, attach_pdf: true });
      toast.success(`Sent to ${list.join(", ")}`);
      onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Couldn't send — try again"); }
    setSending(false);
  };

  const field = "mt-1 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-[13px] outline-none focus:border-orange-500/60";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[460px]" data-testid="analyst-email-dialog">
        <DialogTitle className="text-[15px]">Email this report</DialogTitle>
        <DialogDescription className="text-[12px]">The answer goes in the email body with the PDF attached. Rockpecker addresses only.</DialogDescription>
        <div className="space-y-3 pt-1">
          <label className="block">
            <span className="text-[11.5px] font-semibold an-muted">To</span>
            <input value={to} onChange={(e) => { setTo(e.target.value); setErr(""); }} placeholder="md@rockpecker.com, ops@rockpecker.com"
              className={`${field} h-9`} data-testid="analyst-email-to" />
          </label>
          <label className="block">
            <span className="text-[11.5px] font-semibold an-muted">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${field} h-9`} />
          </label>
          <label className="block">
            <span className="text-[11.5px] font-semibold an-muted">Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Sir, the priority list for this week — top 5 need a decision today."
              className={`${field} py-2 resize-none`} />
          </label>
          {err && <p className="text-[12px] text-red-600 dark:text-red-400" data-testid="analyst-email-error">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg text-[13px] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]">Cancel</button>
            <button type="button" onClick={send} disabled={sending} data-testid="analyst-email-send"
              className="rl-lit-btn h-9 px-4 rounded-lg text-[13px] font-semibold text-white bg-[#f97316] hover:bg-orange-600 inline-flex items-center gap-1.5">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Send
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// "How this was worked out" — the method, folded away until someone wants it.
function MethodNote({ text, customer }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="analyst-method">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="inline-flex items-center gap-1.5 h-7 px-2 -ml-2 rounded-lg text-[12px] font-medium an-muted hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60"
        style={{ fontFamily: SANS }}>
        <Ruler className="w-3.5 h-3.5" />How this was worked out
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="an-swap mt-1 text-[12.5px] leading-relaxed an-muted border-l-2 border-[hsl(var(--border))] pl-3" style={{ fontFamily: SERIF }}>
          {text.replace(/^\s*method:?\s*[—-]?\s*/i, "")}
          {customer && <span className="block mt-0.5" style={{ fontFamily: SANS }}>Shown to you only — left out of the customer PDF.</span>}
        </p>
      )}
    </div>
  );
}

// While streaming, only finished blocks are shown (text up to the last blank
// line) so each block lands once, whole — no half-built tables.
const completePart = (text) => {
  const t = String(text || "");
  const cut = t.lastIndexOf("\n\n");
  return cut < 0 ? "" : t.slice(0, cut);
};

export default function AnswerView({ text, streaming, animate, title, asOf, audience, client, onSave }) {
  const navigate = useNavigate();
  const blocks = useMemo(() => parseAnswer(streaming ? completePart(text) : text), [text, streaming]);
  const fullBlocks = useMemo(() => parseAnswer(text), [text]);
  const [emailOpen, setEmailOpen] = useState(false);
  const leadIndex = blocks.findIndex((b) => b.type === "text");

  const onLink = async (t) => {
    if (t.itemId || t.po) {
      try {
        const { data } = t.po
          ? await axios.get(`${API}/ai/item-link-po`, { params: { po: t.po, line: t.line } })
          : await axios.get(`${API}/ai/item-link/${t.itemId}`);
        t = { path: data.path, item: data.id, q: data.q };
      } catch { toast.error("That item is no longer available to you"); return; }
    }
    navigate(t.path, t.item ? { state: { highlightId: t.item, search: t.q || "", ts: Date.now() } } : undefined);
  };

  const payload = { title, blocks: exportBlocks(fullBlocks), audience, client: client || "", as_of: asOf || "" };
  const pdf = async () => {
    try {
      const { data } = await axios.post(`${API}/ai/report.pdf`, payload, { responseType: "blob" });
      downloadBlob(data, `${(title || "RockLink report").replace(/[^\w -]+/g, "").slice(0, 60) || "RockLink report"}.pdf`);
      return true;
    } catch { toast.error("Couldn't build the PDF"); return false; }
  };
  const copyAll = async () => {
    const { html, text: plainText } = answerToCopy(fullBlocks);
    return copyRich(html, plainText);
  };

  return (
    <div className={animate ? "" : "an-static"} data-testid="analyst-answer">
      <div className="space-y-4">
        {blocks.map((b, i) => {
          let el;
          if (b.type === "kpis") el = <KpiTiles items={b.items} animate={animate} />;
          else if (b.type === "table") el = <ReportTable table={b} title={title} asOf={asOf} onLink={onLink} />;
          else if (b.type === "heading") el = <h3 className="text-[16px] font-semibold" style={{ fontFamily: SERIF }}><Inline text={b.text} onLink={onLink} /></h3>;
          else if (b.type === "list") el = (
            <ul className="space-y-1.5 pl-5 list-disc marker:text-orange-500 text-[14.5px] leading-[1.7]" style={{ fontFamily: SERIF }}>
              {b.items.map((it, j) => <li key={j}><Inline text={it} onLink={onLink} /></li>)}
            </ul>
          );
          else if (b.type === "method") el = <MethodNote text={b.text} customer={audience === "customer"} />;
          else el = (
            <p className="text-[14.5px] leading-[1.7]" style={{ fontFamily: SERIF }}>
              <Inline text={b.text} onLink={onLink} highlight={i === leadIndex} />
            </p>
          );
          return <div key={i} className="an-in">{el}</div>;
        })}
      </div>

      {!streaming && blocks.length > 0 && (
        <div className="an-in flex flex-wrap items-center gap-0.5 mt-5 pt-2 border-t border-[hsl(var(--border))]/70" style={{ animationDelay: "120ms" }} data-testid="analyst-answer-actions">
          <QuietAction icon={Copy} label="Copy answer" doneLabel="Copied" onRun={copyAll} testid="analyst-copy-all" />
          <QuietAction icon={FileText} label="PDF" doneLabel="Downloaded" onRun={pdf} fill testid="analyst-pdf" />
          <QuietAction icon={Mail} label="Email" doneLabel="Email" onRun={async () => { setEmailOpen(true); return false; }} testid="analyst-email" />
          {onSave && <QuietAction icon={Bookmark} label="Save as report" doneLabel="Saved" onRun={onSave} testid="analyst-save" />}
        </div>
      )}
      {emailOpen && <EmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} payload={payload} />}
    </div>
  );
}
